import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Header } from "@/components/converter/Header";
import { Dropzone } from "@/components/converter/Dropzone";
import { ProgressCard } from "@/components/converter/ProgressCard";
import { ErrorCard } from "@/components/converter/ErrorCard";
import { FileQueue } from "@/components/converter/FileQueue";
import { API } from "@/lib/api";
import { filesForm, useFileQueue, validateFiles as validate } from "@/lib/files";
import { setLastJobId } from "@/lib/lastJob";

export default function UploadPage() {
  const navigate = useNavigate();
  const [stage, setStage] = useState("idle");
  const [fileName, setFileName] = useState("");
  const [error, setError] = useState("");
  const [uploadPct, setUploadPct] = useState(0);
  const startedAt = useRef(0);
  const { queue, addFiles, removeAt, clear } = useFileQueue();

  const upload = async (files) => {
    if (!files.length) return;
    const problem = validate(files);
    if (problem) {
      toast.error(problem);
      return;
    }
    setFileName(files.length === 1 ? files[0].name : `${files.length} estratti conto`);
    setUploadPct(0);
    setStage("processing");
    startedAt.current = Date.now();
    const form = filesForm(files);
    try {
      const { data } = await axios.post(`${API}/convert`, form, {
        onUploadProgress: (ev) => ev.total && setUploadPct(Math.round((ev.loaded / ev.total) * 100)),
      });
      setLastJobId(data.job_id);
      navigate(`/tabella/${data.job_id}`);
    } catch (e) {
      setError(e.response?.data?.detail || "Caricamento non riuscito.");
      setStage("error");
    }
  };

  useEffect(() => {
    const prevent = (e) => e.preventDefault();
    const onDrop = (e) => {
      e.preventDefault();
      if (stage === "idle" && e.dataTransfer?.files?.length) addFiles(e.dataTransfer.files);
    };
    window.addEventListener("dragover", prevent);
    window.addEventListener("drop", onDrop);
    return () => {
      window.removeEventListener("dragover", prevent);
      window.removeEventListener("drop", onDrop);
    };
  });

  const reset = () => {
    setStage("idle");
    setError("");
  };

  return (
    <main data-testid="upload-page" className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
      <Header />
      {stage === "idle" && <Dropzone onFile={addFiles} compact={queue.length > 0} />}
      {stage === "idle" && queue.length > 0 && (
        <FileQueue files={queue} onRemove={removeAt} onClear={clear} onStart={() => upload(queue)} />
      )}
      {stage === "processing" && <ProgressCard job={null} uploadPct={uploadPct} fileName={fileName} startedAt={startedAt.current} />}
      {stage === "error" && <ErrorCard message={error} onReset={reset} />}
    </main>
  );
}
